'use client';

import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback, useMemo, useRef } from 'react';
import type { SellerTier } from '@/components/ui/seller-tier';
import type { Storefront } from '@/lib/storefront-sections';
import { 
    onAuthStateChanged,
    signOut, 
    User as FirebaseUser, 
    createUserWithEmailAndPassword, 
    updateProfile,
    signInWithEmailAndPassword, 
    signInWithPopup, 
    GoogleAuthProvider, 
    GithubAuthProvider, 
    linkWithPopup, 
    unlink, 
    EmailAuthProvider, 
    linkWithCredential,
    TotpMultiFactorGenerator,
    getMultiFactorResolver,
    type MultiFactorResolver,
    type TotpSecret,
    multiFactor,
    reauthenticateWithPopup,
    reauthenticateWithCredential,
    type UserCredential,
    reload,
    getIdToken,
    getAdditionalUserInfo
} from 'firebase/auth';
import { doc, onSnapshot, Unsubscribe, updateDoc, getDoc, getDocFromServer, setDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { LOCALE_SETTINGS_DOC, MESSAGING_SETTINGS_DOC, mergePrivateSettings, splitPrivateSettings } from '@/lib/private-settings';
import { useToast } from '@/hooks/use-toast';
import { useRouter } from 'next/navigation';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useFirebase } from '@/firebase';
import { CACHE_WIPE_FLAG } from '@/firebase/client';
import { deleteFCMTokenOnLogout } from '@/firebase/fcm';
import { detectLocaleCurrency } from '@/lib/detect-currency';
import { claimInviteForNewAccount } from '@/lib/invite-link-client';
import type { MessagePrivacy } from '@/lib/message-privacy';
import type { PageDesign } from '@/lib/product-design';
import type { ProfileNote } from '@/lib/profile-notes';
import { userErrorMessage } from '@/lib/user-error-message';

export type SocialLink = {
    name: string;
    href: string;
    placeholder?: string;
    value?: string;
}

export type UserSettings = {
    /** `'system'` follows the OS/browser `prefers-color-scheme`. Default for new users. */
    theme: 'dark' | 'light' | 'system';
    /**
     * The dark-mode starfield, on by default, and the background gradient,
     * off by default (owner, 2026-10-23; older accounts were stamped both on
     * at signup), both always off at checkout; the gate is
     * resolveCosmicEffects in src/lib/cosmic-effects.ts. Both only ever render
     * in dark mode. Typed required, but older documents can lack either key, so
     * ThemeProvider reads them through backgroundPrefs (BACKGROUND_DEFAULTS)
     * rather than trusting them raw.
     */
    showStarfield: boolean;
    showGradient: boolean;
    /**
     * The planets in the starfield, in dark mode: a switch of their own.
     * Absent or null means the default, off (owner, 2026-10-23; on for every
     * device before, except phones from 2026-09-30 to 2026-10-04, when Reset
     * appearance wrote the nulls some accounts still hold). Written by the
     * preferences toggle and by Reset appearance; new accounts are not
     * stamped with it (createVerifiedUserProfile in functions).
     *
     * THE BACKGROUND KEYS HERE ARE SCALARS for the same reason as the
     * storefront keys further down: a map inside `settings` would be deleted
     * by computeUserFieldRepair.
     */
    showPlanets?: boolean | null;
    /**
     * The planets in LIGHT mode, kept apart from the dark-mode switch above so
     * each mode keeps its own choice (owner decision 2026-09-30). Absent means
     * off. It replaced `showPlanetsLight` on 2026-10-26, when the owner turned
     * light-mode planets off for everyone; older documents may still hold that
     * key, and nothing reads it. Light mode's starfield switch, `showStarfieldLight`, was retired when
     * the dot grid replaced it (owner, 2026-10-23); older documents may still
     * hold it, and nothing reads it.
     */
    showPlanetsInLight?: boolean;
    /**
     * Light mode's background, the dot grid (src/components/ui/dot-grid.tsx).
     * Absent means on. Never shown in dark mode, at checkout or on a seller's
     * page. See backgroundPrefs in src/lib/background-prefs.ts.
     */
    showDotGrid?: boolean;
    /**
     * Where the background gradient comes from (it only renders in dark mode).
     * '' or absent means automatic, from below. Never auto-written: only an
     * explicit pick stores a direction, and Automatic stores '' (never
     * deleteField(), which updateUser would merge into React state as a
     * sentinel). See src/lib/background-prefs.ts.
     */
    gradientDirection?: 'top' | 'bottom' | '';
    /**
     * The background gradient's own colour, '#rrggbb', independent of
     * accentColor. '' or absent means automatic: the painted accent. Never
     * auto-written, unlike accentColor: storing the automatic value would
     * freeze it, and the gradient would stop following the accent.
     */
    gradientColor?: string;
    /** Empty string means "auto-detect from browser locale". */
    dateFormat: 'DD/MM/YYYY' | 'YYYY/MM/DD' | 'MM/DD/YYYY' | '';
    accentColor: string;
    currency: string;
    showNotificationBubbles: boolean;
    /** Show the banner image at the top of the profile. Off by default. */
    showProfileBanner: boolean;
    /**
     * THE avatar switch, storefront included. Only the Account settings toggle
     * turns it off; the Design card on /dashboard/profile turns it back on
     * whenever a placement is picked (avatarPlacementPatch).
     */
    showProfilePicture: boolean;
    /**
     * Where the avatar goes on the public storefront while showProfilePicture
     * is on: the page body, the storefront nav, or both. Absent means both
     * (owner decision 2026-09-28; see resolveAvatarPlacement), and 'none' is
     * never written. Always written together with showProfilePicture through
     * avatarPlacementPatch, never alone.
     *
     * THE STOREFRONT KEYS BELOW ARE SCALARS ON PURPOSE. firestore.rules
     * allowlists `settings` as a whole, and computeUserFieldRepair in functions
     * drops any settings value that is not a string, number or boolean (and
     * truncates a string past 200 characters). Nesting them in a map, or
     * storing the social placements as lists, would typecheck, pass rules, and
     * then be deleted by the onUserUpdated trigger. See
     * src/lib/storefront-chrome.ts.
     */
    avatarPlacement?: 'both' | 'profile' | 'nav';
    /**
     * Social link platform ids shown in the storefront nav bar,
     * comma-separated ("instagram,youtube"); '' means none. Written with
     * serializeSocialLinkPlacement in the same updateUser call as socialLinks.
     * Absent means the legacy storefrontNavSocials decides (see
     * resolveSocialLinkPlacement).
     */
    navSocialLinks?: string;
    /**
     * Social link platform ids shown below the bio on the public profile, same
     * format as navSocialLinks. Absent means every link, as before placements
     * existed; '' means none.
     */
    bioSocialLinks?: string;
    /**
     * LEGACY. The old all-or-nothing "social links in your nav bar" switch. No
     * UI writes it any more; it is read only as the nav default while
     * navSocialLinks is absent. (The retired storefrontFullWidth is also still
     * on some documents and is ignored, so it is deliberately not declared.)
     */
    storefrontNavSocials?: boolean;
    /**
     * Show the country the seller registered in on their storefront. Absent
     * means off, and nothing stamps it on new profiles. Only the switch lives
     * here; the country stays in the owner-read sellerProfiles/{uid}, and the
     * storefront's server render reads it only while this is true (see
     * src/lib/storefront-location.ts).
     */
    showLocation?: boolean;
    /**
     * Where the location goes on the storefront while showLocation is on.
     * Absent means the profile only. Written together with showLocation
     * through locationPlacementPatch, never alone (src/lib/storefront-chrome.ts).
     */
    locationPlacement?: 'both' | 'profile' | 'nav';
    /**
     * Where the username shows on the storefront: the nav bar, the profile's
     * heading, or both. Absent means both.
     */
    namePlacement?: 'both' | 'profile' | 'nav';
    /**
     * Where the joined date shows on the storefront, or 'hidden'. Absent means
     * the profile only, under the name.
     */
    joinedPlacement?: 'both' | 'profile' | 'nav' | 'hidden';
    opaqueMessages: boolean;
    showToast: boolean;
    toastDurationMs: number;
    /** IETF BCP-47 code (e.g. 'en', 'pt-BR'). Empty string means "auto-detect from browser". */
    language: string;
    /** IANA timezone (e.g. 'Europe/London'). Empty string means "auto-detect from browser". */
    timezone: string;
    isMfaEnabled?: boolean;
};

type User = {
    username: string;
    avatarUrl: string;
    /** Storage path of the currently displayed (cropped) avatar, used for cleanup on re-edit */
    avatarPath?: string;
    avatarZoom?: number;
    /** Original (pre-crop) avatar URL — stored for re-editing with react-easy-crop */
    avatarOriginalUrl?: string;
    /** Storage path of the original avatar, used for cleanup when a new original is uploaded */
    avatarOriginalPath?: string;
    /** react-easy-crop pixel crop area saved at last edit */
    avatarCroppedAreaPixels?: { x: number; y: number; width: number; height: number } | null;
    /** Displayed (cropped) banner URL. Empty string when no banner is set. */
    bannerUrl?: string;
    /** Storage path of the currently displayed (cropped) banner, used for cleanup on re-edit */
    bannerPath?: string;
    bannerZoom?: number;
    /** Original (pre-crop) banner URL — stored for re-editing with react-easy-crop */
    bannerOriginalUrl?: string;
    /** Storage path of the original banner, used for cleanup when a new original is uploaded */
    bannerOriginalPath?: string;
    /** react-easy-crop pixel crop area saved at last edit */
    bannerCroppedAreaPixels?: { x: number; y: number; width: number; height: number } | null;
    email: string;
    emailVerified: boolean;
    bio: string;
    about: string;
    website: string;
    socialLinks: SocialLink[];
    /** Public, owner-authored profile notes. See src/lib/profile-notes.ts. */
    notes?: ProfileNote[];
    /**
     * Server-stamped clock for the note-creation rate limit. firestore.rules
     * requires this to equal request.time whenever it changes, so it is only
     * ever written as serverTimestamp() — never a client-chosen value. Typed
     * loosely because the write carries a FieldValue sentinel while a read
     * returns a Firestore Timestamp; nothing renders it.
     */
    lastNoteAt?: unknown;
    /**
     * Named, ordered sections on the seller's public products tab. Absent means
     * one plain grid, which is what the tab was before sections existed. See
     * src/lib/storefront-sections.ts for the shape and what happens to a
     * product that is in no section.
     */
    storefront?: Storefront;
    /**
     * Who this account has muted, by uid, from its owner-only
     * users/{uid}/settings/messaging (src/lib/private-settings.ts). Merged in
     * by the provider; never on the public document.
     */
    mutedUids?: string[];
    /**
     * Seller-controlled gate on inbound messages. Absent = `everybody` (the
     * historical default). See src/lib/message-privacy.ts for the option set
     * and the shared decision function.
     */
    messagePrivacy?: MessagePrivacy;
    /**
     * The seller's storefront brand: accent colour, background colour, the
     * two background effects, fonts and an optional forced light/dark for their
     * PUBLIC PROFILE page. The same map a product carries, resolved by the same
     * `resolveDesignOverride`, and it cascades to that seller's product pages
     * for everything except a forced theme; a background colour brings its own
     * light or dark with it (see resolveDesignWithSellerBrand in
     * src/lib/product-design.ts).
     *
     * Absent on every account that has not opened the design card, which is the
     * default and means visitors see the plain page in their own theme.
     */
    design?: PageDesign;
    createdAt: string;
    settings: UserSettings;
    sellerTier: SellerTier | null;
    isDiamond: boolean;
    foundingSeller?: boolean;
    /**
     * Set to `false` by createVerifiedUserProfile on brand-new accounts; the
     * one-time "confirm your detected regional settings" dialog reads this and
     * flips it to `true` once the user responds. Absent on pre-existing
     * accounts, so they are never prompted.
     */
    localePrefsConfirmed?: boolean;
    /**
     * Gates the internal Developer console at /dashboard/staff/dev.
     * Server-managed: written ONLY by an admin directly in the Firestore
     * console (Admin SDK bypasses rules) — firestore.rules blocks every client
     * write to this key so a user cannot self-promote. Absent/false for
     * everyone by default.
     * NOTE: this only unlocks the UI. Every dev-console callable independently
     * re-checks staffRoles/{uid}.isDeveloper server-side, so a forged client cannot
     * reach privileged data by flipping this in memory.
     */
    isDeveloper?: boolean;
    /**
     * Gates the internal Admin (moderation) console at /dashboard/staff/admin.
     * Exactly the same trust model as isDeveloper above: Admin-SDK /
     * Firestore-console write only, blocked in BOTH the create and update
     * denylists of firestore.rules, cosmetic on the client, and re-checked
     * server-side by assertAdmin/assertStaff on every admin callable.
     *
     * Deliberately a SEPARATE flag rather than a level above isDeveloper: the
     * two consoles answer to different jobs (moderation vs diagnostics), and a
     * support operator who moderates should not thereby gain the ledger,
     * treasury and HMRC filing tools.
     */
    isAdmin?: boolean;
}

/**
 * What updateUser accepts: any client-writable profile fields, plus an optional
 * PARTIAL settings patch, in one call and so in one Firestore write.
 *
 * Typed as one object rather than the old `Partial<User> | { settings }` union.
 * That union let a mixed literal such as `{ design, settings: patch }` match the
 * settings-only branch structurally, which left `design` completely unchecked
 * (and at runtime the design never reached Firestore at all; see updateUser).
 */
export type UserUpdate = Omit<Partial<User>, 'settings'> & { settings?: Partial<UserSettings> };

/**
 * Defensive strip: these fields are written exclusively by Cloud Functions
 * (Admin SDK) and rejected by firestore.rules if a client attempts them
 * directly. Username goes through the updateUsername callable;
 * email/emailVerified are mirrored via syncUserEmail / notifyEmailVerified
 * after Firebase Auth changes.
 *
 * A client-side mirror of part of the firestore.rules update blocklist, kept
 * narrow on purpose: it only lists fields a caller might plausibly pass by
 * accident, so they get stripped here instead of failing the rule, which would
 * reject the WHOLE write (every other field in the call included) and desync
 * React state from Firestore. The money aggregates (sellerBalance,
 * totalRevenue, totalPayouts, totalSales, totalPlatformFees) are blocked by the
 * rules too but are never in any call site's field set, so they stay out of
 * this list. Stripped whether or not the call also carries settings (it used to
 * be skipped for any call with settings).
 */
const SERVER_MANAGED_FIELDS = ['username', 'email', 'emailVerified'] as const;

/**
 * Split one updateUser argument into the top-level fields to write and the
 * settings patch, without mutating the caller's object.
 *
 * settings.isMfaEnabled is dropped from the patch here, for both the write and
 * the optimistic state: it mirrors Firebase Auth, only the Admin-SDK callables
 * may change it, and the rules pin it, so a client write carrying it would be
 * rejected outright.
 */
function splitUserUpdate(update: UserUpdate): {
  fields: Omit<Partial<User>, 'settings'>;
  settingsPatch: Partial<UserSettings> | null;
  blocked: string[];
} {
  const { settings, ...rest } = update;
  const fields: Omit<Partial<User>, 'settings'> = { ...rest };
  const blocked: string[] = [];
  for (const key of SERVER_MANAGED_FIELDS) {
    if (key in fields) {
      blocked.push(key);
      delete (fields as Record<string, unknown>)[key];
    }
  }
  const settingsPatch = settings && typeof settings === 'object'
    ? (Object.fromEntries(Object.entries(settings).filter(([key]) => key !== 'isMfaEnabled')) as Partial<UserSettings>)
    : null;
  return { fields, settingsPatch, blocked };
}

/**
 * The single updateDoc payload for one updateUser call: every top-level field,
 * plus the settings patch either as dotted `settings.<key>` paths (a merge,
 * which leaves every other stored setting alone) or, under replaceSettings, as
 * the whole replacement map.
 *
 * A replacement re-attaches the STORED isMfaEnabled when there is one and
 * carries none when there is not, because the update rule rejects any change
 * to that key, adding it included.
 */
function buildUserUpdatePayload(
  fields: Omit<Partial<User>, 'settings'>,
  settingsPatch: Partial<UserSettings> | null,
  replaceSettings: boolean,
  storedSettings: unknown,
): Record<string, unknown> {
  const payload: Record<string, unknown> = { ...fields };
  if (!settingsPatch) return payload;
  if (replaceSettings) {
    const replacement: Record<string, unknown> = { ...settingsPatch };
    delete replacement.isMfaEnabled;
    if (storedSettings && typeof storedSettings === 'object' && 'isMfaEnabled' in storedSettings) {
      replacement.isMfaEnabled = (storedSettings as { isMfaEnabled?: unknown }).isMfaEnabled;
    }
    payload.settings = replacement;
  } else {
    for (const [key, value] of Object.entries(settingsPatch)) {
      if (key === 'isMfaEnabled') continue;
      payload[`settings.${key}`] = value;
    }
  }
  return payload;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null | undefined; // undefined means loading
  authUser: FirebaseUser | null;
  authError: string | null;
  clearAuthError: () => void;
  isUserLoading: boolean;
  /** Resolves to whether the write landed; never rejects. See updateUser. */
  updateUser: (updatedFields: UserUpdate, options?: { replaceSettings?: boolean }) => Promise<boolean>;
  /** `referralCode`: a code from the signup page's optional Referral code box (src/lib/invite-link.ts). */
  signup: (email: string, password: string, username?: string, referralCode?: string | null) => Promise<void>;
  login: (emailOrUsername: string, password: string) => Promise<void>;
  /** `referralCode` as for signup: it credits the account if this first sign-in creates one. */
  loginWithGoogle: (referralCode?: string | null) => void;
  logout: () => Promise<void>;
  sellerTier: SellerTier | null;
  isDiamond: boolean;
  isFoundingSeller: boolean;
  /**
   * The staffRoles listener has answered for the signed-in account, so
   * `user.isAdmin` and `user.isDeveloper` are its own answer and not the
   * default false. What must never draw for an admin and then vanish (the
   * seller setup checklist, src/lib/admin-sellers.ts) waits for it.
   */
  staffRolesLoaded: boolean;
  linkProvider: (providerName: 'google' | 'github') => Promise<void>;
  unlinkProvider: (userToUnlink: FirebaseUser, providerId: string) => Promise<void>;
  addPassword: (password: string) => Promise<void>;
  /** Mute an account, by uid (users/{uid}/settings/messaging). */
  muteUser: (uid: string) => void;
  unmuteUser: (uid: string) => void;
  mfaResolver: MultiFactorResolver | null;
  setMfaResolver: React.Dispatch<React.SetStateAction<MultiFactorResolver | null>>;
  mfaLoginEmail: string | null;
  setMfaLoginEmail: React.Dispatch<React.SetStateAction<string | null>>;
  start2faEnrollment: () => Promise<{ secret: TotpSecret; qrCodeUri: string } | null>;
  /**
   * Store a fresh set of recovery codes and return them. Resolves null on a
   * failure it has already toasted. REJECTS only with the server's re-auth
   * challenge ('reauth-required' / 'mfa-reauth-required'): storeRecoveryCodes
   * needs a sign-in from the last five minutes, and the caller answers that by
   * re-authenticating and calling again (see useReauthGate), which a toast
   * saying "try again" could never achieve.
   */
  generateAndStoreRecoveryCodes: () => Promise<string[] | null>;
  complete2faEnrollment: (secret: TotpSecret, verificationCode: string) => Promise<boolean>;
  disable2fa: (userToUpdate: FirebaseUser) => Promise<void>;
  submitRecoveryCode: (recoveryCode: string, email?: string, options?: { keepUserLoggedIn?: boolean }) => Promise<void>;
  completeMfa: (verificationCode: string) => Promise<UserCredential | null>;
  reauthenticateWithPassword: (password: string) => Promise<UserCredential | 'mfa-required' | 'error'>;
  reauthenticateWithProvider: (providerName: 'google' | 'github') => Promise<UserCredential | 'mfa-required' | 'error'>;
  sendVerificationBeforeEmailUpdate: (newEmail: string) => Promise<void>;
  sendCurrentUserEmailVerification: (options?: { silent?: boolean }) => Promise<void>;
  refreshAuthUser: () => Promise<void>;
  triggerProfileCreation: () => Promise<void>;
  /** One-shot sync: read Firestore user doc and push it into React state now. */
  syncUserFromFirestoreNow: (uid: string) => Promise<void>;
}

// Web Storage can be unavailable outright: a browser with site data blocked
// throws a SecurityError on the first touch of `localStorage`, including from
// `typeof localStorage`, which reads the same throwing getter. Everything this
// provider keeps there is a best-effort signup hint, so a throw is read as
// "absent" instead of taking the whole provider (and so every page) down.
function readLocal(key: string): string | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
  } catch {
    return null;
  }
}

function writeLocal(key: string, value: string): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(key, value);
  } catch { /* storage unavailable */ }
}

function removeLocal(...keys: string[]): void {
  try {
    if (typeof localStorage === 'undefined') return;
    for (const key of keys) localStorage.removeItem(key);
  } catch { /* storage unavailable */ }
}

const SIGNUP_HINT_KEYS = ['awaitingEmailVerification', 'pendingUsername', 'pendingUserCurrency', 'pendingUserDateFormat'];

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [isUserLoading, setIsUserLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const [sellerTier, setSellerTierState] = useState<SellerTier | null>(null);
  const [isDiamond, setIsDiamondState] = useState(false);
  const [isFoundingSeller, setIsFoundingSellerState] = useState(false);
  const [authUserState, setAuthUserState] = useState<FirebaseUser | null>(null);
  const [mfaResolver, setMfaResolver] = useState<MultiFactorResolver | null>(null);
  const [mfaLoginEmail, setMfaLoginEmail] = useState<string | null>(null);
  /**
   * The staff flags, read from their OWN owner-readable document rather than
   * from users/{uid}.
   *
   * They used to live on the user doc, which is `allow read: if true` — and
   * that grants `list`, so `where('isDeveloper','==',true)` from a signed-out
   * browser returned the staff roster. staffRoles/{uid} is `isOwner`, which
   * makes a collection-wide list unprovable and therefore denied.
   *
   * `null` means "no staffRoles document", NOT "not staff": that is the
   * un-migrated case, and the merge below then falls back to the legacy fields
   * still on the user doc, exactly as readStaffRoles does server-side. Both
   * fallbacks retire together once backfillPrivateSellerFields has run.
   */
  const [staffRoles, setStaffRoles] = useState<{ isDeveloper: boolean; isAdmin: boolean } | null>(null);
  // The uid the staffRoles listener last answered for (an error is an answer
  // too), so `staffRolesLoaded` can say the flags above are this account's.
  const [staffRolesFor, setStaffRolesFor] = useState<string | null>(null);
  /**
   * The account's owner-only settings, kept off its public document
   * (src/lib/private-settings.ts): users/{uid}/settings/locale (time zone,
   * display currency) and settings/messaging (who it has muted, by uid).
   * Merged into `user` at the point of consumption, like staffRoles. Null
   * locale means not loaded yet, which leaves the public settings as they are.
   */
  const [privateLocale, setPrivateLocale] = useState<Record<string, unknown> | null>(null);
  const [mutedUids, setMutedUids] = useState<string[]>([]);
  const hasShownUnverifiedToastRef = useRef(false);
  const authChannelRef = useRef<BroadcastChannel | null>(null);
  const lastAuthUidRef = useRef<string | null>(null);
  // Dedupes concurrent profile-creation attempts (React StrictMode double-
  // effect, or two sibling components both calling triggerProfileCreation).
  // Per-provider ref instead of module-scope so the map doesn't survive HMR
  // and can't leak across unrelated AuthProvider instances in tests.
  const profileCreationInProgressRef = useRef<{ [uid: string]: boolean }>({});
  // True once we've observed `accountDeleting: true` on the user doc.
  // The server flips this flag before deleting the doc, so when the snapshot
  // listener later sees the doc disappear we already know it's a deletion in
  // progress — not a fresh signup that needs a profile created.
  const accountDeletingRef = useRef(false);

  const { auth, firestore: db } = useFirebase();
  const { toast } = useToast();
  const router = useRouter();

  // Read the Firestore user doc and push it into React state immediately,
  // bypassing the onSnapshot listener's latency. The listener will eventually
  // fire too and set the same state — this is a belt-and-suspenders sync for
  // the verification flow, where callers need `user` populated quickly so the
  // dashboard layout's loader can resolve.
  const syncUserFromFirestoreNow = useCallback(async (uid: string) => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;
    const userDocRef = doc(db, 'users', uid);
    // getDocFromServer bypasses the local cache. Critical during the
    // verification flow: if the onSnapshot listener's Listen channel
    // hiccups during the concurrent token refreshes (we've seen it return
    // 400), the cache gets stuck at "doesn't exist" from the initial
    // pre-profile listener fire, and a regular getDoc would read that
    // stale cached miss even though the profile does exist on the server.
    const snap = await getDocFromServer(userDocRef).catch(() => null);
    if (!snap || !snap.exists()) return;
    const profileData = snap.data() as User;
    const mergedEmailVerified =
      profileData.emailVerified === true || currentUser.emailVerified === true;
    const hasMfaEnabledInAuth = multiFactor(currentUser).enrolledFactors.length > 0;
    setUser({
      ...profileData,
      email: currentUser.email || profileData.email,
      emailVerified: mergedEmailVerified,
      settings: { ...profileData.settings, isMfaEnabled: hasMfaEnabledInAuth },
    });
    setSellerTierState(profileData.sellerTier);
    setIsDiamondState(profileData.isDiamond || false);
    setIsFoundingSellerState(profileData.foundingSeller || false);
    setIsUserLoading(false);
  }, [auth, db]);

 const triggerProfileCreation = useCallback(async () => {
    const currentUser = auth.currentUser;
    if (!currentUser || !currentUser.emailVerified) return;

    // Bail out if the account is mid-deletion. The server flips
    // accountDeleting=true and then deletes the user doc; the snapshot
    // listener observes the doc gone and sets `user` to null, which makes
    // the dashboard layout call this trigger as if the user needed a fresh
    // profile. Recreating one would race the delete and surface a
    // "Profile Creation Failed" toast on top of the deletion confirmation.
    if (accountDeletingRef.current) return;

    if (profileCreationInProgressRef.current[currentUser.uid]) return;

    const userDocRef = doc(db, 'users', currentUser.uid);
    // Server read, not `getDoc`. With the persistent IndexedDB cache enabled
    // (src/firebase/client.ts) a plain getDoc can be answered from disk, and
    // the entry cached during the pre-profile window is a "doesn't exist"
    // miss — which would send us down the create branch for an account whose
    // profile already exists on the server.
    //
    // If the server read itself fails (offline, transient), fall through to
    // the create branch rather than stalling: `createVerifiedUserProfile` is
    // idempotent and its `already-exists` result is handled below.
    const docSnap = await getDocFromServer(userDocRef).catch(() => null);

    if (!docSnap || !docSnap.exists()) {
        // Prefer displayName (set during signup) but fall back to the username
        // captured in localStorage at signup time. During the verification
        // flow Firebase can briefly return a user whose displayName hasn't
        // propagated yet; without a fallback, valid signups would land on
        // the dashboard loader without ever creating a profile.
        const storedUsername = readLocal('pendingUsername');
        const candidateUsername = currentUser.displayName || storedUsername || '';
        // Only forward the candidate to the server when it's already a valid
        // username (email/password signup chose one upfront). For OAuth
        // (Google, GitHub) the displayName is the user's real name —
        // "John Smith" — which isn't a legal username. In that case we send
        // NO username and the server auto-generates a `userNNNNNN` handle.
        // The user can rename it later from /dashboard/settings/account.
        const isValidUsername = !!candidateUsername && /^[a-zA-Z0-9_]{3,20}$/.test(candidateUsername);
        const usernameToSubmit = isValidUsername ? candidateUsername : undefined;

        profileCreationInProgressRef.current[currentUser.uid] = true;
        const functions = getFunctions();
        const createProfile = httpsCallable(functions, 'createVerifiedUserProfile');
        // Currency: pass the browser-locale hint as a soft fallback. The
        // Cloud Function preferentially resolves from the request IP via
        // MaxMind GeoLite2, so a US-English browser sitting on an Australian
        // IP still ends up with AUD. The hint only wins if IP geo fails.
        // dateFormat is intentionally not sent — the product default is
        // YYYY/MM/DD regardless of locale.
        const pendingCurrency = readLocal('pendingUserCurrency') || detectLocaleCurrency();
        const clearSignupHints = () => removeLocal(...SIGNUP_HINT_KEYS);
        try {
            // Intentionally no getIdToken(true) here. The Cloud Function reads
            // emailVerified via admin.auth().getUser() (authoritative server
            // read) rather than from the token claim, so we don't need to
            // force-refresh client-side. Avoiding the token rotation keeps
            // Firestore's WebChannel from dropping into offline mode during
            // the verification flow.
            await createProfile({
                ...(usernameToSubmit ? { username: usernameToSubmit } : {}),
                currency: pendingCurrency,
            });
            clearSignupHints();
            // Push the freshly-created profile into React state now so the
            // caller can navigate to /dashboard without waiting for the
            // onSnapshot listener to round-trip.
            await syncUserFromFirestoreNow(currentUser.uid);
        } catch (err: any) {
            console.error("Automatic profile creation failed:", err);
            // `already-exists` means EXACTLY ONE THING: the username we asked for
            // was taken. It does not mean the profile already exists — that case
            // returns `{ success: true }`, not an error (see
            // createVerifiedUserProfile). This branch used to read it the other
            // way round, clear the signup hints and re-sync, which found no
            // document and left `user` null forever: the dashboard layout then
            // spun on "Setting up your account…" with no error, no retry (its
            // own triggeredRef blocks one) and no way to reach the rename
            // screen, which is also gated on `user`. The account was
            // unrecoverable without support.
            //
            // A username collision must never be terminal, so retry WITHOUT one
            // and let the server allocate a handle — the same path every OAuth
            // signup already takes. The user lands in the dashboard with a
            // profile and is told what happened and where to change it, instead
            // of staring at a spinner.
            if (err.code === 'functions/already-exists') {
                try {
                    // ONE CASE FIRST: we lost a race to our own other tab.
                    //
                    // The signup and the email-link tab can both call this with
                    // the same pending username. The winner claims
                    // usernames/{lower} and then writes users/{uid}; the loser
                    // gets already-exists from that claim, and if it retried
                    // immediately it could slip into the gap before the winner's
                    // profile write lands and mint a SECOND username for the same
                    // account. The gap has no awaits in it, so this is a short
                    // poll rather than a long one — and on the ordinary
                    // collision (the name really is someone else's) the profile
                    // never appears and we fall through to the retry below.
                    //
                    // Worth distinguishing because the outcomes differ: here the
                    // user got the username they asked for, so there is nothing
                    // to apologise for and no toast.
                    for (let i = 0; i < 4; i++) {
                        const raced = await getDocFromServer(doc(db, 'users', currentUser.uid))
                            .catch(() => null);
                        if (raced?.exists()) {
                            clearSignupHints();
                            await syncUserFromFirestoreNow(currentUser.uid);
                            return;
                        }
                        if (i < 3) await new Promise(r => setTimeout(r, 250));
                    }
                    await createProfile({ currency: pendingCurrency });
                    clearSignupHints();
                    await syncUserFromFirestoreNow(currentUser.uid);
                    // Says WHY, which the server's username_assigned
                    // notification (which names the handle they got) cannot: it
                    // runs for every auto-generated handle and has no idea a
                    // chosen one was refused.
                    toast({
                        title: 'That username was already taken',
                        description: usernameToSubmit
                            ? `Someone already has "${usernameToSubmit}", so we picked a different one to finish setting up your account. You can change it in Account Settings.`
                            : 'We picked a username so we could finish setting up your account. You can change it in Account Settings.',
                    });
                } catch (retryErr) {
                    console.error('Fallback profile creation failed:', retryErr);
                    toast({
                        title: 'Could not create your profile',
                        description: 'Reload the page to try again. If it keeps happening, contact support.',
                        variant: 'destructive',
                    });
                }
            } else {
                 toast({
                    title: "Could not create your profile",
                    description: "Reload the page to try again. If it keeps happening, contact support.",
                    variant: 'destructive',
                });
            }
        } finally {
            delete profileCreationInProgressRef.current[currentUser.uid];
        }
    } else {
        // Profile already exists — clear any stale signup hints and sync it
        // into React state in case the listener hasn't fired yet.
        removeLocal(...SIGNUP_HINT_KEYS);
        await syncUserFromFirestoreNow(currentUser.uid);
    }
  }, [auth, db, toast, syncUserFromFirestoreNow]);


  useEffect(() => {
    let unsubscribeFromUser: Unsubscribe | null = null;
    let unsubscribeFromStaffRoles: Unsubscribe | null = null;
    let unsubscribeFromPrivate: Unsubscribe | null = null;

    // Cross-tab logout: Firebase's IndexedDB polling propagates sign-out in
    // ~1-5s, which is too slow on shared machines. A BroadcastChannel cuts
    // that to <50ms by telling sibling tabs to sign out immediately.
    if (typeof BroadcastChannel !== 'undefined' && !authChannelRef.current) {
        const ch = new BroadcastChannel('auth');
        ch.onmessage = (event) => {
            if (event.data?.type === 'logout' && auth.currentUser) {
                signOut(auth).catch(() => { /* onAuthStateChanged will still fire */ });
            } else if (event.data?.type === 'email-verified' && auth.currentUser && auth.currentUser.uid === event.data?.uid) {
                auth.currentUser.reload().catch(() => { /* next interaction will retry */ });
            }
        };
        authChannelRef.current = ch;
    }

    const unsubscribeAuth = onAuthStateChanged(auth, async (currentAuthUser) => {
        // Clone the user object to ensure React detects the change
        setAuthUserState(currentAuthUser ? { ...currentAuthUser } as FirebaseUser : null);

        // Only flip isUserLoading back to true when the signed-in uid actually
        // changes (initial sign-in, sign-out, or account switch). Firebase
        // fires onAuthStateChanged on unrelated state updates too (e.g. after
        // applyActionCode in another tab propagates via IndexedDB) — resetting
        // the loading flag on every fire leaves pages stuck on "Redirecting…"
        // for the duration of the downstream awaits below.
        const prevUid = lastAuthUidRef.current;
        const nextUid = currentAuthUser?.uid ?? null;
        const uidChanged = prevUid !== nextUid;
        lastAuthUidRef.current = nextUid;
        if (currentAuthUser && uidChanged) {
            setIsUserLoading(true);
        }

        // Auth identity changed (sign-in, sign-out, or account switch). Drop any
        // guest-session artifacts so the new identity cannot inherit ownership
        // recorded for a prior guest purchase in this tab. Without this, a guest
        // who bought product X and then signed up or logged in with a different
        // email would still see product X as owned via sessionStorage and
        // retain download access via the guestDlToken cookie.
        if (uidChanged && typeof window !== 'undefined') {
            try {
                sessionStorage.removeItem('guestOwnedProducts');
                sessionStorage.removeItem('guestOrderMap');
                // Drop any in-progress product drafts from the previous session
                // so the create/edit forms don't hydrate someone else's work.
                // Keyed off a persisted lastAuthUid (not lastAuthUidRef, which
                // resets to null on refresh): a same-user refresh must not
                // wipe their own draft. Covers the close-browser-without-logout
                // → different-user-login path that the logout() clear misses.
                const currentUid = currentAuthUser?.uid ?? null;
                const lastKnownUid = localStorage.getItem('lastAuthUid');
                if (lastKnownUid && currentUid && lastKnownUid !== currentUid) {
                    const toRemove: string[] = [];
                    for (let i = 0; i < localStorage.length; i++) {
                        const key = localStorage.key(i);
                        if (key && key.startsWith('draftProduct:')) toRemove.push(key);
                    }
                    toRemove.forEach(k => localStorage.removeItem(k));
                }
                if (currentUid) localStorage.setItem('lastAuthUid', currentUid);
            } catch { /* ignore storage failures */ }
            // The guestDlToken is httpOnly (Path=/api/download), so JS cannot
            // clear it directly — the server route resets it with maxAge=0.
            fetch('/api/guest/clear-download-token', { method: 'POST' }).catch(() => {});
        }

        // Only tear down the Firestore listener when the uid changes — otherwise
        // we'd drop the live subscription during unrelated auth events (like
        // emailVerified flipping) and leave pages without a `user` value.
        if (uidChanged && unsubscribeFromUser) {
            unsubscribeFromUser();
            unsubscribeFromUser = null;
        }
        if (uidChanged && unsubscribeFromStaffRoles) {
            unsubscribeFromStaffRoles();
            unsubscribeFromStaffRoles = null;
            setStaffRoles(null);
            setStaffRolesFor(null);
        }
        if (uidChanged) {
            if (unsubscribeFromPrivate) {
                unsubscribeFromPrivate();
                unsubscribeFromPrivate = null;
            }
            setPrivateLocale(null);
            setMutedUids([]);
        }

        if (currentAuthUser) {
            const userDocRef = doc(db, 'users', currentAuthUser.uid);

            // Staff flags, from their own owner-readable document. Separate
            // listener because it is a separate document now — see the
            // staffRoles state declaration for why they left users/{uid}.
            //
            // A missing document is the ordinary case (almost nobody is staff)
            // and is NOT an error: the rule is isOwner(uid), which evaluates
            // fine against a document that does not exist, so this neither
            // throws nor logs for a normal account. It resolves to null, and
            // the merge below then consults the legacy user-doc fields.
            if (!unsubscribeFromStaffRoles) {
                unsubscribeFromStaffRoles = onSnapshot(
                    doc(db, 'staffRoles', currentAuthUser.uid),
                    (roleSnap) => {
                        const data = roleSnap.exists() ? roleSnap.data() : null;
                        setStaffRoles(data
                            ? { isDeveloper: data.isDeveloper === true, isAdmin: data.isAdmin === true }
                            : null);
                        setStaffRolesFor(currentAuthUser.uid);
                    },
                    (error) => {
                        // Never fail open. Leaving staffRoles at null falls back
                        // to the legacy fields, and if those are gone the user
                        // simply sees no Staff nav entry — the server callables
                        // are the real gate either way.
                        console.error('Firestore staffRoles onSnapshot error:', error);
                        setStaffRoles(null);
                        setStaffRolesFor(currentAuthUser.uid);
                    },
                );
            }

            // The owner-only settings (src/lib/private-settings.ts). A missing
            // document is ordinary: an account that never chose a currency or
            // muted anyone has neither.
            if (!unsubscribeFromPrivate) {
                const settingsDoc = (id: string) => doc(db, 'users', currentAuthUser.uid, 'settings', id);
                const unsubscribeLocale = onSnapshot(
                    settingsDoc(LOCALE_SETTINGS_DOC),
                    (snap) => setPrivateLocale(snap.exists() ? snap.data() : {}),
                    (error) => console.error('Firestore locale settings onSnapshot error:', error),
                );
                const unsubscribeMessaging = onSnapshot(
                    settingsDoc(MESSAGING_SETTINGS_DOC),
                    (snap) => {
                        const list = snap.exists() ? snap.data().mutedUids : undefined;
                        setMutedUids(Array.isArray(list) ? list.filter((u): u is string => typeof u === 'string') : []);
                    },
                    (error) => console.error('Firestore messaging settings onSnapshot error:', error),
                );
                unsubscribeFromPrivate = () => { unsubscribeLocale(); unsubscribeMessaging(); };
            }

            // Attach the onSnapshot listener FIRST (if not already attached)
            // so `user` can be populated by the listener even while the async
            // triggerProfileCreation below is still running. Previously we
            // awaited profile creation before subscribing, which left the
            // dashboard's guard stuck on the loader for the Cloud Function's
            // entire round-trip.
            if (!unsubscribeFromUser) {
                unsubscribeFromUser = onSnapshot(
                    userDocRef,
                    (docSnap) => {
                        if (docSnap.exists()) {
                            const profileData = docSnap.data() as User & { accountDeleting?: boolean };
                            // Mirror the server-set delete flag into a ref so
                            // triggerProfileCreation knows to bail once the
                            // doc subsequently disappears.
                            accountDeletingRef.current = profileData.accountDeleting === true;
                            const hasMfaEnabledInAuth = multiFactor(currentAuthUser).enrolledFactors.length > 0;

                            // Firestore's emailVerified is authoritative when
                            // it's true — it was written after a server-side
                            // applyActionCode succeeded. The local cached user
                            // can lag (e.g. the /auth/action tab hasn't called
                            // reload() yet), so merge toward the "true" side
                            // rather than regressing Firestore.
                            const mergedEmailVerified =
                                profileData.emailVerified === true || currentAuthUser.emailVerified === true;

                            // `emailVerified` is Admin-SDK only (firestore.rules
                            // privileged blocklist) so a forged client write
                            // can't desync it from Firebase Auth. Route any
                            // divergence through the syncUserEmail callable,
                            // which reads admin.auth() and writes it.
                            //
                            // The email ADDRESS is no longer mirrored to the
                            // (world-readable) users doc — it is read straight
                            // from Firebase Auth (currentUser.email). So we only
                            // sync on emailVerified divergence; comparing the
                            // now-absent profileData.email would otherwise fire
                            // syncUserEmail on every load.
                            const needsEmailSync =
                                (profileData.emailVerified !== true && currentAuthUser.emailVerified === true);
                            // Skip the sync if the account is mid-deletion.
                            // The server has already flipped accountDeleting
                            // and is about to drop both the auth user and the
                            // Firestore doc; firing syncUserEmail into that
                            // race surfaces a 500 (auth/user-not-found) for
                            // a flow that is otherwise succeeding (e.g. the
                            // "Start over" path on /awaiting-verification).
                            if (needsEmailSync && !accountDeletingRef.current) {
                                try {
                                    const callable = httpsCallable(getFunctions(), 'syncUserEmail');
                                    callable({}).catch(e => console.error("Error syncing auth state to Firestore:", e));
                                } catch (e) {
                                    console.error("Error invoking syncUserEmail:", e);
                                }
                            }

                            // If Firestore says verified but the local cache
                            // still says not, kick a reload so the rest of the
                            // app sees fresh state — but don't show the
                            // "Email Not Verified" toast in that case: the
                            // email *is* verified; local state just lags.
                            if (profileData.emailVerified === true && currentAuthUser.emailVerified !== true) {
                                currentAuthUser.reload().catch(() => { /* next interaction will retry */ });
                            }

                            // Show once per session, only when BOTH sources
                            // agree the email is unverified.
                            if (!mergedEmailVerified && !hasShownUnverifiedToastRef.current) {
                                hasShownUnverifiedToastRef.current = true;
                                toast({
                                    title: "Email not verified",
                                    description: "Please verify your email address in Account Settings to access all features.",
                                });
                            } else if (mergedEmailVerified) {
                                hasShownUnverifiedToastRef.current = false;
                            }

                            setUser({
                                ...profileData,
                                email: currentAuthUser.email || profileData.email,
                                emailVerified: mergedEmailVerified,
                                settings: { ...profileData.settings, isMfaEnabled: hasMfaEnabledInAuth },
                            });
                            setSellerTierState(profileData.sellerTier);
                            setIsDiamondState(profileData.isDiamond || false);
                            setIsFoundingSellerState(profileData.foundingSeller || false);
                        } else {
                            // The doc doesn't exist. Only regress user state to
                            // null if this snapshot is authoritative (from the
                            // server). A cache-only miss can happen while the
                            // WebChannel is offline — during the verification
                            // flow that would wipe a just-populated user and
                            // strand the dashboard on its loader. In that
                            // case, leave `user` alone and let
                            // syncUserFromFirestoreNow (server read) decide.
                            if (!docSnap.metadata.fromCache) {
                                setUser(null);
                            }
                        }
                        setIsUserLoading(false);
                    },
                    (error) => {
                        console.error("Firestore onSnapshot error:", error);
                        setIsUserLoading(false);
                    },
                );
            }

            if (currentAuthUser.emailVerified) {
                hasShownUnverifiedToastRef.current = false;
                // Skip automatic profile creation if this is a fresh signup that
                // is still awaiting real email verification. The awaiting-verification
                // page will call triggerProfileCreation() once the user actually
                // clicks the link. This prevents Firebase's stale emailVerified=true
                // state (carried over from a previous account on the same email)
                // from bypassing the verification requirement.
                const awaitingVerification = readLocal('awaitingEmailVerification');
                if (awaitingVerification !== currentAuthUser.uid) {
                    // Fire-and-forget: the onSnapshot listener (already attached
                    // above) will set `user` as soon as the Cloud Function
                    // creates the doc. Awaiting here would leave `isUserLoading`
                    // pinned to true for the whole round-trip.
                    triggerProfileCreation().catch(() => { /* toast already shown */ });
                }
            }
            // The "not verified" toast is shown inside onSnapshot so we can first
            // confirm a Firestore profile exists. Brand-new signups have no doc yet
            // and should land on /awaiting-verification silently, not see a toast.
        } else {
            setUser(null);
            setStaffRoles(null);
            setStaffRolesFor(null);
            setSellerTierState(null);
            setIsDiamondState(false);
            setIsFoundingSellerState(false);
            setAuthError(null);
            setIsUserLoading(false);
            profileCreationInProgressRef.current = {};
            hasShownUnverifiedToastRef.current = false;
            accountDeletingRef.current = false;
        }
    });

    return () => {
        unsubscribeAuth();
        if (unsubscribeFromUser) {
            unsubscribeFromUser();
        }
        if (unsubscribeFromStaffRoles) {
            unsubscribeFromStaffRoles();
        }
        if (unsubscribeFromPrivate) {
            unsubscribeFromPrivate();
        }
        if (authChannelRef.current) {
            authChannelRef.current.close();
            authChannelRef.current = null;
        }
    };
  }, [auth, db, toast, triggerProfileCreation]);


 const signup = useCallback(async (
    email: string, password: string, username?: string, referralCode?: string | null,
 ) => {
    try {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        if (userCredential.user) {
            // Credit the new account to a referral link, if any: the code typed
            // into the signup page's Referral code box, else the link it
            // arrived through (src/lib/invite-link.ts). Sent now, while it is
            // signed in and before the email is verified, so a verification
            // finished on another device still counts. Not awaited, and it
            // never throws: it must not slow or break the signup.
            void claimInviteForNewAccount(typeof referralCode === 'string' ? referralCode : null);
            await updateProfile(userCredential.user, { displayName: username });
            // localStorage (not sessionStorage) so the flag is visible to the
            // /auth/action tab the magic link opens. sessionStorage is per-tab:
            // without cross-tab visibility, the new tab's onAuthStateChanged
            // would fire with emailVerified=true (after applyActionCode) and
            // race the original tab to call triggerProfileCreation.
            //
            // Best effort: with storage blocked the flag is simply absent, and
            // createVerifiedUserProfile still refuses an unverified account on
            // its own authoritative read of emailVerified.
            writeLocal('pendingUserCurrency', detectLocaleCurrency());
            writeLocal('awaitingEmailVerification', userCredential.user.uid);
            if (username) writeLocal('pendingUsername', username);
            // The verification email is sent by the `onAuthUserCreated` Auth
            // trigger ([functions/src/index.ts]) within seconds of the user
            // record being created. Don't also fire the `sendVerificationEmail`
            // callable here — it would double up the `generateEmailVerificationLink`
            // call (counted against the per-project Identity Toolkit quota)
            // AND deliver two identical emails to the new user. The callable
            // remains deployed for the "Resend" button on /awaiting-verification,
            // which is user-initiated and rate-limited at 5/hour per uid.
        } else {
            throw new Error("User creation failed.");
        }
    } catch (error: any) {
        if (error.code === 'auth/email-already-in-use') {
            toast({
                title: "Could not sign up",
                description: "This email is already associated with an account. Please log in.",
                variant: "destructive",
            });
        } else {
            const friendly = userErrorMessage(error, "Something went wrong. Try again in a moment.");
            setAuthError(friendly);
            toast({
                title: "Could not sign up",
                description: friendly,
                variant: "destructive",
            });
        }
        throw error;
    }
    // The awaiting-verification page auto-sends via Resend on first visit,
    // so we skip the Firebase SDK send here. The Cloud Function handles branding.
  }, [auth, toast]);

  const login = useCallback(async (emailOrUsername: string, password: string) => {
    setAuthError(null);
    setMfaResolver(null);
    let email = emailOrUsername;

    if (!emailOrUsername.includes('@')) {
        const functions = getFunctions();
        const getUserByEmailOrUsernameCallable = httpsCallable(functions, 'getUserByEmailOrUsername');
        try {
            const result = await getUserByEmailOrUsernameCallable({ username: emailOrUsername });
            const data = result.data as { email: string | null };
            if (data.email) {
                email = data.email;
            } else {
                // This branch is reached only when the input had no "@", i.e.
                // the user typed a username — saying "email address" here sent
                // people off to check an email they never entered.
                setAuthError("No account found with that username.");
                return;
            }
        } catch (error) {
            // The lookup itself failed (App Check, rate limit, network). Don't
            // claim the account doesn't exist — that misdirects the user into
            // resetting a password or re-registering.
            setAuthError("Could not verify that username. Try again, or log in with your email address.");
            return;
        }
    }

    try {
      await signInWithEmailAndPassword(auth, email, password);
      setAuthError(null);
    } catch (error: any) {
       if (error.code === 'auth/multi-factor-auth-required') {
            const resolver = getMultiFactorResolver(auth, error);
            setMfaLoginEmail(email);
            setMfaResolver(resolver);
       } else if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
          setAuthError("That email or username and password don't match. Check them and try again.");
       } else {
          setAuthError("An unexpected error occurred during login.");
       }
    }
  }, [auth]);

  const socialProviderLogin = useCallback(async (
    provider: GoogleAuthProvider | GithubAuthProvider, referralCode: string | null,
  ) => {
    setAuthError(null);
    try {
        const credential = await signInWithPopup(auth, provider);
        // A first Google or GitHub sign-in is what creates the account: credit
        // it to a referral link, if any, as signup() does. The code was read
        // when the button was pressed, so it cannot be lost to the signup page
        // leaving while the popup closes.
        if (getAdditionalUserInfo(credential)?.isNewUser) void claimInviteForNewAccount(referralCode);
    } catch (error: any) {
        if (error.code === 'auth/multi-factor-auth-required') {
            const resolver = getMultiFactorResolver(auth, error);
            if (error.customData?.email) {
                setMfaLoginEmail(error.customData.email);
            }
            setMfaResolver(resolver);
            return;
        }

        if (error.code !== 'auth/popup-closed-by-user' && error.code !== 'auth/cancelled-popup-request') {
            toast({
                title: "Could not log in",
                description: userErrorMessage(error, "Try again in a moment."),
                variant: "destructive"
            });
        }
    }
  }, [auth, toast]);

  const loginWithGoogle = useCallback((referralCode?: string | null) => {
    const provider = new GoogleAuthProvider();
    // Force the account-chooser every time. Without this, Google auto-selects
    // the only signed-in account and skips the picker entirely — which on
    // signup means the user never sees which account they're creating with.
    provider.setCustomParameters({ prompt: 'select_account' });
    // Only a string is a code: a button wired straight to onClick hands in
    // its click event.
    return socialProviderLogin(provider, typeof referralCode === 'string' ? referralCode : null);
  }, [socialProviderLogin]);

  const linkProvider = useCallback(async (providerName: 'google' | 'github') => {
    if (!auth.currentUser) return;

    const provider = providerName === 'google' ? new GoogleAuthProvider() : new GithubAuthProvider();
    if (provider instanceof GoogleAuthProvider) {
        provider.setCustomParameters({ prompt: 'select_account' });
    }
    try {
        const result = await linkWithPopup(auth.currentUser, provider);
        setAuthUserState(result.user ? { ...result.user } as FirebaseUser : null);
        toast({
            title: "Account linked",
            description: `You can now log in with ${providerName}.`,
        });

    } catch (error: any) {
        if (error.code === 'auth/multi-factor-auth-required') {
            const resolver = getMultiFactorResolver(auth, error);
            setMfaResolver(resolver);
            return;
        }

        if (error.code === 'auth/credential-already-in-use') {
            toast({
                title: "Could not link the account",
                description: "This social account is already linked to another user.",
                variant: "destructive",
            });
            return;
        }
        
        if (error.code === 'auth/popup-closed-by-user' || error.code === 'cancelled-popup-request') {
            return; 
        }
        
        toast({
            title: "Could not link the account",
            description: userErrorMessage(error, "Try again in a moment."),
            variant: "destructive",
        });
    }
  }, [auth, toast]);

  const unlinkProvider = useCallback(async (userToUnlink: FirebaseUser, providerId: string) => {
    try {
        const updatedUser = await unlink(userToUnlink, providerId);
        setAuthUserState(updatedUser ? { ...updatedUser } as FirebaseUser : null);
        toast({ title: "Account unlinked", description: "You can no longer log in this way." });
    } catch (error: any) {
         toast({ title: "Could not unlink the account", description: userErrorMessage(error, "Try again in a moment."), variant: "destructive" });
    }
  }, [toast]);

  const addPassword = useCallback(async (password: string) => {
    const user = auth.currentUser;
    if (!user) {
        toast({ title: "Not logged in", description: "You must be logged in to add a password.", variant: "destructive" });
        return;
    }
    const userEmail = user.email;
    if (typeof userEmail !== 'string') {
        toast({ title: "Email not found", description: "Cannot add password without a user email.", variant: "destructive" });
        return;
    }
    try {
        const credential = EmailAuthProvider.credential(userEmail, password);
        const result = await linkWithCredential(user, credential);
        // Linking an EmailAuthProvider credential to a previously-OAuth-only
        // user flips Firebase Auth's `emailVerified` to false (Firebase treats
        // the password credential as a new identity assertion). The email is
        // already verified by Google / another trusted OIDC provider, so
        // server-side flip it back via Admin SDK — otherwise the next dashboard
        // route the user hits bounces them to /awaiting-verification.
        try {
            const callable = httpsCallable(getFunctions(), 'markEmailVerifiedIfTrustedProvider');
            await callable({});
            // Token's `email_verified` claim is set at mint time. Force-refresh
            // so subsequent reads see the restored state without waiting for
            // the next ~1h auto-rotation.
            try { await getIdToken(result.user, true); } catch { /* non-fatal */ }
            try { await result.user.reload(); } catch { /* non-fatal */ }
        } catch { /* non-fatal — the user can still verify via email */ }
        setAuthUserState(result.user ? { ...result.user } as FirebaseUser : null);
        toast({ title: "Password added", description: "You can now log in with your email and password." });
    } catch (error: any) {
         toast({ title: "Could not add the password", description: userErrorMessage(error, "Try again in a moment."), variant: "destructive" });
    }
  }, [auth, toast]);

  const logout = useCallback(async () => {
    const priorUid = auth.currentUser?.uid;
    if (priorUid) {
      delete profileCreationInProgressRef.current[priorUid];
      // Unregister the device's FCM token before auth state drops so push
      // notifications for this user don't keep arriving on a shared
      // browser after sign-out. Best-effort — never block logout on it.
      await deleteFCMTokenOnLogout(priorUid).catch(() => {});
    }
    await signOut(auth);
    authChannelRef.current?.postMessage({ type: 'logout' });
    // Clear any in-progress product drafts plus pending-signup state so the
    // next user on this device doesn't hydrate the create/edit form from
    // the previous session's work and doesn't inherit a stale username /
    // currency / awaitingVerification uid if they start a fresh signup.
    try {
      if (typeof window !== 'undefined') {
        const toRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith('draftProduct:')) toRemove.push(key);
        }
        toRemove.forEach(k => localStorage.removeItem(k));
        localStorage.removeItem('pendingUsername');
        localStorage.removeItem('pendingUserCurrency');
        localStorage.removeItem('pendingUserDateFormat');
        localStorage.removeItem('awaitingEmailVerification');
        // Drop the per-user accent so the next person on this device — or
        // a guest reload before auth resolves — doesn't see the prior
        // user's colour. theme-context's authUser-null effect resets state
        // in-memory; this purge ensures the pre-hydration script in
        // src/app/layout.tsx falls back to the default on next reload too.
        localStorage.removeItem('accentColor');
        // Mark the Firestore IndexedDB cache dirty. The previous session's
        // documents (profile, cart, notifications, messages) are still on
        // disk; src/firebase/client.ts wipes them on a later app boot, the
        // first that finds no other tab open, before any listener attaches.
        // It can't be done here: clearing requires terminating the Firestore
        // instance, which would leave the rest of this SPA session unable to
        // read anything.
        localStorage.setItem(CACHE_WIPE_FLAG, '1');
      }
    } catch {}
    // onAuthStateChanged (above) has already fired by the time `signOut`
    // resolves and set authUser=null / user=null / isUserLoading=false.
    // Manually re-clearing those here regressed `user` from null back to
    // undefined (= "loading"), which made downstream `!user` guards keep
    // showing the loader after navigation. Leave the listener's writes in
    // place; only clear the local-only MFA scratch state.
    setMfaResolver(null);
    setMfaLoginEmail(null);
  }, [auth]);

  /**
   * Write profile fields and/or a settings patch to the signed-in user's
   * document, in ONE updateDoc, and mirror them into React state optimistically.
   *
   * A MIXED CALL IS ONE WRITE. `{ socialLinks, settings: { navSocialLinks,
   * bioSocialLinks } }` and `{ design, settings: avatarPlacementPatch(p) }` are
   * the two callers that need it: the list and its placements, or the design
   * and the avatar placement when one Save changed both, must land together
   * or a visitor can load the page between two writes and see them out of
   * step. Until 2026-09-28 this
   * function took the settings branch whenever `settings` was present and
   * wrote ONLY the dotted settings keys, while React state took every field:
   * the other fields looked saved until the next snapshot and never reached
   * Firestore. Every field now goes in the same payload (see
   * buildUserUpdatePayload).
   *
   * Rules-wise a mixed write is the same as its parts: affectedKeys() is the
   * union of top-level keys ('settings' plus the others), which
   * clientWritableUserFields() already allows, and userFieldBoundsOk() checks
   * each changed key on its own. settings.isMfaEnabled is never written from
   * here, so its pin in the update rule holds.
   *
   * RESOLVES TO WHETHER IT SAVED, AND NEVER REJECTS. `true` once the write has
   * landed (or when there was nothing to write); `false` when there is no
   * signed-in user or no user document, or when the write failed, in which
   * case the "Update Failed" toast has already been shown. Callers that only
   * fire and forget can ignore it, as most do. An editor that closes on save
   * should close only on `true`, so a failed write (a rules rejection, or a
   * rules change deployed after the app) leaves the draft in front of the
   * owner instead of throwing it away.
   *
   * On failure it puts back its own optimistic values where they are still
   * exactly as it set them (undoOptimistic), and the document listener does
   * the rest: Firestore drops a rejected local write and emits the stored
   * document again, a macrotask AFTER this has resolved (the SDK raises write
   * callbacks before listen events, and delivers listen events to user code
   * on a timer). A form that re-seeds from `user` must allow for that late
   * snapshot (see keepDraftRef in the Design card).
   */
  const updateUser = useCallback(async (updatedFields: UserUpdate, options?: { replaceSettings?: boolean }): Promise<boolean> => {
    if (!auth.currentUser) return false;

    const { fields, settingsPatch: requestedSettings, blocked } = splitUserUpdate(updatedFields);
    if (blocked.length > 0) {
        console.warn(`[updateUser] ignoring server-managed fields: ${blocked.join(', ')}`);
    }
    // The time zone and currency are written to the owner-only locale doc, never
    // the public one, which firestore.rules refuses them on (src/lib/
    // private-settings.ts). Its listener updates the merged `user` as soon as
    // the local write is queued, so they need no optimistic step of their own.
    const { publicPatch: settingsPatch, privatePatch } = splitPrivateSettings(requestedSettings);
    if (!settingsPatch && !privatePatch && Object.keys(fields).length === 0) return true;
    const replaceSettings = options?.replaceSettings === true;

    // What the optimistic update below replaced, recorded by its own updater
    // so a failed write can put exactly that back (undoOptimistic). React runs
    // queued updaters in order, so the undo's updater always runs after this
    // one has recorded it.
    let replaced: {
        fields: Record<string, unknown>;
        settings: UserSettings | undefined;
        optimisticSettings: UserSettings | undefined;
    } | null = null;

    // The same fields the write below carries, so state and document agree
    // once the snapshot lands. isMfaEnabled is derived from Firebase Auth by
    // the snapshot listener, so a replacement keeps the value already in state
    // rather than dropping it until the next snapshot.
    setUser(prevUser => {
        if (!prevUser) return null;
        let settings = prevUser.settings;
        if (settingsPatch) {
            const mfa = prevUser.settings && 'isMfaEnabled' in prevUser.settings
                ? { isMfaEnabled: prevUser.settings.isMfaEnabled }
                : {};
            settings = (replaceSettings
                ? { ...settingsPatch, ...mfa }
                : { ...prevUser.settings, ...settingsPatch }) as UserSettings;
        }
        replaced = {
            fields: Object.fromEntries(Object.keys(fields).map(key => [key, (prevUser as Record<string, unknown>)[key]])),
            settings: prevUser.settings,
            optimisticSettings: settings,
        };
        return { ...prevUser, ...fields, settings };
    });

    // Put back what the optimistic update replaced, for a write that failed.
    // Only values still EXACTLY as this call left them (same reference): once
    // the document listener has delivered a snapshot, including the one for
    // the pending local write, that snapshot's values are the document's own
    // answer and are left for its rollback snapshot to correct. Without this a
    // failure that never reached Firestore (the read before the write failing,
    // or the write refused as invalid before it was queued) left the unsaved
    // values in state as if saved, since no snapshot follows one of those.
    const undoOptimistic = () => setUser(current => {
        const undo: typeof replaced = replaced;
        if (!current || !undo) return current;
        const next: Record<string, unknown> = { ...current };
        let changed = false;
        for (const [key, value] of Object.entries(fields)) {
            if ((current as Record<string, unknown>)[key] === value) {
                next[key] = undo.fields[key];
                changed = true;
            }
        }
        if (settingsPatch && current.settings === undo.optimisticSettings) {
            next.settings = undo.settings;
            changed = true;
        }
        return changed ? (next as User) : current;
    });

    try {
      const userDocRef = doc(db, 'users', auth.currentUser.uid);
      const docSnap = await getDoc(userDocRef);

      if (!docSnap.exists()) {
          undoOptimistic();
          return false;
      }
      const payload = buildUserUpdatePayload(fields, settingsPatch, replaceSettings, docSnap.data()?.settings);
      if (Object.keys(payload).length > 0) {
          await updateDoc(userDocRef, payload);
      }
      if (privatePatch) {
          // A reset replaces the locale too, as it does the public settings.
          await setDoc(doc(db, 'users', auth.currentUser.uid, 'settings', LOCALE_SETTINGS_DOC),
              privatePatch, replaceSettings ? {} : { merge: true });
      }
      return true;
    } catch(e) {
       console.error('[updateUser] error:', e);
       undoOptimistic();
       toast({ title: "Could not save your changes", description: "Check your connection and try again.", variant: 'destructive'});
       return false;
    }
  }, [auth, db, toast]);

  // Mutes are the account's own, by uid, in users/{uid}/settings/messaging
  // (src/lib/private-settings.ts): never on the public profile, where anyone
  // could read who had muted whom, and a rename cannot slip one. The listener
  // updates `user.mutedUids` as soon as the write is queued.
  const writeMute = useCallback((uid: string, muted: boolean) => {
    const me = auth.currentUser;
    if (!me || !uid || uid === me.uid) return;
    setDoc(doc(db, 'users', me.uid, 'settings', MESSAGING_SETTINGS_DOC),
        { mutedUids: muted ? arrayUnion(uid) : arrayRemove(uid) }, { merge: true })
        .catch((e) => {
            console.error('[muteUser] error:', e);
            toast({ title: "Could not save your changes", description: "Check your connection and try again.", variant: 'destructive' });
        });
  }, [auth, db, toast]);
  const muteUser = useCallback((uid: string) => writeMute(uid, true), [writeMute]);
  const unmuteUser = useCallback((uid: string) => writeMute(uid, false), [writeMute]);

  const reauthenticateWithPassword = useCallback(async (password: string): Promise<UserCredential | 'mfa-required' | 'error'> => {
    if (!auth.currentUser || !auth.currentUser.email) {
        toast({ title: "Could not confirm it is you", description: "No user is currently logged in.", variant: "destructive" });
        return 'error';
    }
    setMfaResolver(null);

    try {
        const credential = EmailAuthProvider.credential(auth.currentUser.email, password);
        const userCredential = await reauthenticateWithCredential(auth.currentUser, credential);
        return userCredential;
    } catch (error: any) {
        if (error.code === 'auth/multi-factor-auth-required') {
            const resolver = getMultiFactorResolver(auth, error);
            setMfaResolver(resolver);
            return 'mfa-required';
        }
        if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        } else {
            toast({ title: "Could not confirm it is you", description: userErrorMessage(error, "Try again in a moment."), variant: "destructive" });
        }
        return 'error';
    }
  }, [auth, toast]);

  const reauthenticateWithProvider = useCallback(async (providerName: 'google' | 'github'): Promise<UserCredential | 'mfa-required' | 'error'> => {
      if (!auth.currentUser) return 'error';
      setMfaResolver(null);
      const provider = providerName === 'google' ? new GoogleAuthProvider() : new GithubAuthProvider();
      try {
          const userCredential = await reauthenticateWithPopup(auth.currentUser, provider);
          return userCredential;
      } catch (error: any) {
          if (error.code === 'auth/multi-factor-auth-required') {
            const resolver = getMultiFactorResolver(auth, error);
            setMfaResolver(resolver);
            return 'mfa-required';
          }
          if (error.code !== 'auth/popup-closed-by-user' && error.code !== 'auth/cancelled-popup-request') {
            toast({ title: "Could not log in", description: userErrorMessage(error, "Try again in a moment."), variant: "destructive" });
          }
          return 'error';
      }
  }, [auth, toast]);

    const start2faEnrollment = useCallback(async (): Promise<{ secret: TotpSecret; qrCodeUri: string } | null> => {
        const currentUser = auth.currentUser;
        if (!currentUser) return null;
        
        const userEmail = currentUser.email;
        if (typeof userEmail !== 'string') {
          throw new Error("User email is not available for MFA setup.");
        }

        try {
            const mfaSession = await multiFactor(currentUser).getSession();
            const secret = await TotpMultiFactorGenerator.generateSecret(mfaSession);
            const qrCodeUri = secret.generateQrCodeUrl(
                userEmail,
                'supa.place'
            );
            return { secret, qrCodeUri };
        } catch (error: any) {
            throw error;
        }
    }, [auth]);

    const generateAndStoreRecoveryCodes = useCallback(async (): Promise<string[] | null> => {
        if (!auth.currentUser) {
            toast({ title: "Not logged in", description: "You must be logged in to generate recovery codes.", variant: "destructive" });
            return null;
        }

        try {
            const functions = getFunctions();
            const storeRecoveryCodes = httpsCallable(functions, 'storeRecoveryCodes');
            const result = await storeRecoveryCodes({});
            const codes = (result.data as { codes: string[] }).codes;
            return codes;
        } catch (error) {
            // The re-auth challenge is not a failure to report. Retrying cannot
            // clear it (auth_time only moves on a fresh sign-in), so it goes back
            // to the caller, which re-authenticates and stores again.
            const message = error instanceof Error ? error.message : '';
            if (message === 'reauth-required' || message === 'mfa-reauth-required') throw error;
            console.error("Failed to store recovery codes:", error);
            toast({ title: "Could not save recovery codes", description: "Try again in a moment.", variant: "destructive" });
            return null;
        }
    }, [auth, toast]);
    
    const complete2faEnrollment = useCallback(async (secret: TotpSecret, verificationCode: string): Promise<boolean> => {
        if (!auth.currentUser) return false;
        try {
            const assertion = TotpMultiFactorGenerator.assertionForEnrollment(secret, verificationCode);
            await multiFactor(auth.currentUser).enroll(assertion, 'Authenticator App');
            // The Firestore mirror (settings.isMfaEnabled) is written by the
            // notifyMfaEnabled callable below — Admin SDK only, since
            // firestore.rules now blocks client writes to that nested field.
            // The snapshot listener also derives the live UI value from
            // multiFactor(authUser).enrolledFactors regardless, so the mirror
            // is purely a server-authored cache.
            try {
                const notify = httpsCallable(getFunctions(), 'notifyMfaEnabled');
                await notify({});
            } catch { /* non-fatal */ }
            // Force a one-shot user-state sync so the UI flips from
            // "Enable" → "Disable" without waiting for the Firestore
            // snapshot listener to fire (which can be hundreds of ms
            // behind, long enough for the dialog to close on stale state).
            // syncUserFromFirestoreNow reads admin-side state and derives
            // isMfaEnabled from multiFactor(authUser).enrolledFactors,
            // which has been updated synchronously by enroll() above.
            try { await syncUserFromFirestoreNow(auth.currentUser.uid); } catch { /* non-fatal */ }
            return true;
        } catch (error: any) {
            if (error.code !== 'auth/mfa-enrollment-already-complete') {
                toast({ title: "Could not verify the code", description: "The code was incorrect. Enter the current one from your authenticator app.", variant: "destructive" });
            }
            return false;
        }
    }, [auth, toast, syncUserFromFirestoreNow]);

    const disable2fa = useCallback(async (userToUpdate: FirebaseUser) => {
        try {
            const factors = multiFactor(userToUpdate).enrolledFactors;
            const totpFactor = factors.find(
                f => f.factorId === TotpMultiFactorGenerator.FACTOR_ID
            );

            // No factor means nothing to unenroll. The Firestore mirror is
            // server-managed (notifyMfaDisabled writes via Admin SDK), so we
            // don't touch settings.isMfaEnabled from the client either way.
            if (!totpFactor) return;

            await multiFactor(userToUpdate).unenroll(totpFactor.uid);

            // Flip the UI immediately. The downstream cleanup (clearRecoveryCodes,
            // notifyMfaDisabled, syncUserFromFirestoreNow) takes hundreds of ms,
            // and the snapshot listener won't fire until notifyMfaDisabled writes
            // to Firestore — so without this the toggle stays on "Disable" until
            // a manual refresh.
            setUser(prev => prev ? { ...prev, settings: { ...prev.settings, isMfaEnabled: false } } : prev);

            // Wipe the user's hashed recovery codes — they were minted to
            // disable this exact factor, and leaving them attached to a
            // now-disabled account makes the recovery-code path a footgun
            // if MFA is later re-enabled. The legacy code called
            // storeRecoveryCodes({ codes: [] }) but that callable IGNORES
            // its input and *generates* 10 fresh codes — net effect was
            // 10 orphan codes per disable. clearRecoveryCodes just deletes.
            const functions = getFunctions();
            const clearCodes = httpsCallable(functions, 'clearRecoveryCodes');
            await clearCodes({}).catch(e => console.warn('Failed to clear recovery codes:', e));

            await userToUpdate.reload();

            const hasMfa = auth.currentUser ? multiFactor(auth.currentUser).enrolledFactors.length > 0 : false;

            if (!hasMfa) {
                toast({ title: "Two-factor authentication turned off", description: "Your authenticator app is no longer needed when you log in." });
                try {
                    const notify = httpsCallable(getFunctions(), 'notifyMfaDisabled');
                    await notify({});
                } catch { /* non-fatal */ }
            }

            // Force a one-shot user-state sync so the UI flips from
            // "Disable" → "Enable" without waiting for the Firestore
            // snapshot listener. See note in complete2faEnrollment.
            if (auth.currentUser) {
                try { await syncUserFromFirestoreNow(auth.currentUser.uid); } catch { /* non-fatal */ }
            }
        } catch (error: any) {
            toast({ title: "Could not turn off two-factor authentication", description: userErrorMessage(error, "Try again in a moment."), variant: "destructive" });
        }
    }, [auth, toast, syncUserFromFirestoreNow]);

  const refreshAuthUser = useCallback(async () => {
      if (!auth.currentUser) return;
      try {
          await reload(auth.currentUser);
      } catch (e: any) {
          // Polling-friendly: rate-limit, transient network blips, and revoked-
          // token errors all mean "skip this tick." The reload is called every
          // few seconds from awaiting-verification and account settings, so
          // surfacing these would spam the console for no user benefit. A
          // genuinely invalidated user is handled by the next onAuthStateChanged
          // tick, which signs them out cleanly.
          const code = e?.code;
          if (
              code === 'auth/too-many-requests' ||
              code === 'auth/network-request-failed' ||
              code === 'auth/internal-error' ||
              code === 'auth/user-token-expired' ||
              code === 'auth/invalid-user-token' ||
              code === 'auth/user-not-found' ||
              code === 'auth/user-disabled'
          ) return;
          throw e;
      }
      const freshUser = auth.currentUser;
      // Re-snapshot after reload so React sees a new object reference with updated properties.
      setAuthUserState({ ...freshUser } as FirebaseUser);
      // Sync emailVerified into the Firestore user state immediately so both
      // sources agree without waiting for the next onSnapshot tick.
      setUser(prev => prev ? { ...prev, emailVerified: freshUser.emailVerified } : prev);
  }, [auth]);

  const submitRecoveryCode = useCallback(async (recoveryCode: string, email?: string, options: { keepUserLoggedIn?: boolean } = {}): Promise<void> => {
    const { keepUserLoggedIn = false } = options;
    const functions = getFunctions();
    const submitRecoveryCodeCallable = httpsCallable(functions, 'useRecoveryCode');

    const emailForCall = email || auth.currentUser?.email;
    if (!emailForCall) {
        const err = new Error("We couldn't tell which account this is for. Log in again, then use the code.");
        toast({
            title: "Could not use the recovery code",
            description: err.message,
            variant: "destructive",
        });
        throw err;
    }

    // Phase 1 — the callable itself. Only failures here should surface the
    // destructive "Recovery Failed" toast. Anything that goes wrong AFTER the
    // server has accepted the code (logout side-effects, router push, etc.) is
    // post-success cleanup and must not be reported as a recovery failure.
    try {
        await submitRecoveryCodeCallable({ recoveryCode, email: emailForCall });
    } catch (error: any) {
        toast({
            title: "Could not use the recovery code",
            description: userErrorMessage(error, "Try again in a moment."),
            variant: "destructive",
        });
        throw error;
    }

    // Phase 2 — success. Show the confirmation toast and immediately drop the
    // MFA-pending state so the OTP dialog can't linger or reopen if the
    // subsequent logout/refresh side-effects throw. The server has already
    // removed the MFA factor; the dialog is meaningless from this point on.
    toast({
        title: "Two-factor authentication turned off",
        description: keepUserLoggedIn
            ? "Two-factor authentication has been turned off."
            : "Two-factor authentication has been turned off. Please log in again.",
    });
    setMfaResolver(null);
    setMfaLoginEmail(null);

    if (keepUserLoggedIn) {
        // Flip the UI immediately. refreshAuthUser only reloads the
        // FirebaseUser; settings.isMfaEnabled in our `user` state would
        // otherwise wait for the Firestore snapshot listener to round-trip.
        setUser(prev => prev ? { ...prev, settings: { ...prev.settings, isMfaEnabled: false } } : prev);
        try { await refreshAuthUser(); } catch { /* non-fatal post-success cleanup */ }
    } else {
        try { await logout(); } catch { /* non-fatal post-success cleanup */ }
        router.push('/login');
    }
  }, [auth, toast, refreshAuthUser, logout, router]);

    const completeMfa = useCallback(async (verificationCode: string): Promise<UserCredential | null> => {
        if (!mfaResolver) return null;
        try {
            const hint = mfaResolver.hints.find(h => h.factorId === TotpMultiFactorGenerator.FACTOR_ID) ?? mfaResolver.hints[0];
            const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, verificationCode);
            const userCredential = await mfaResolver.resolveSignIn(assertion);
            
            await userCredential.user.reload();
            
            setMfaResolver(null);
            setMfaLoginEmail(null);
            return userCredential;
        } catch (error: any) {
            toast({ title: "Incorrect code", description: "Enter the current code from your authenticator app.", variant: "destructive" });
            return null;
        }
    }, [mfaResolver, toast]);

  const sendVerificationBeforeEmailUpdate = useCallback(async (newEmail: string) => {
    if (!auth.currentUser) {
        throw new Error("No user is currently logged in.");
    }

    if (!auth.currentUser.emailVerified) {
        throw new Error("Current email not verified.");
    }

    // Route through the `initiateEmailChange` callable (Admin SDK) instead of
    // Firebase's client-only `verifyBeforeUpdateEmail`. The callable rate-
    // limits by uid, generates the verify-and-change link server-side, sends
    // the verification email to the NEW address, AND — critically — notifies
    // the OLD address with a password-reset link so an attacker with a
    // stolen session can't silently rebind the account to an address they
    // control. It also drops an in-app `account_security` notification.
    try {
        const functions = getFunctions();
        const callable = httpsCallable(functions, 'initiateEmailChange');
        await callable({ newEmail });
    } catch (error: any) {
        // The callable is gated by requireRecentReauth, whose two challenge codes
        // are recoverable: the caller reopens the reauth dialog and replays. Only
        // toast real failures, or the user sees the raw 'reauth-required' string.
        // Checked inline rather than via isReauthRequiredError to avoid a circular
        // import (use-reauth-gate imports useAuth from this module).
        const msg = String(error?.message ?? '');
        if (msg !== 'reauth-required' && msg !== 'mfa-reauth-required') {
            toast({ title: "Could not send the verification email", description: userErrorMessage(error, "Try again in a moment."), variant: "destructive" });
        }
        throw error;
    }
  }, [auth, toast]);

  const sendCurrentUserEmailVerification = useCallback(async (options?: { silent?: boolean }) => {
    if (!auth.currentUser) {
        throw new Error("No user is currently logged in.");
    }
    try {
        const functions = getFunctions();
        const sendVerificationEmail = httpsCallable(functions, 'sendVerificationEmail');
        await sendVerificationEmail({});
    } catch (error: any) {
        if (!options?.silent) {
            toast({
                title: "Could not send the verification email",
                description: userErrorMessage(error, "Try again in a moment."),
                variant: "destructive",
            });
        }
        throw error;
    }
  }, [auth, toast]);

  const clearAuthError = useCallback(() => setAuthError(null), []);

  // Stable identity for the context value so consumers using a single
  // `useAuth()` slice don't re-render on every parent state change. All
  // action handlers above are wrapped in useCallback with explicit deps, so
  // this memo only invalidates when their captured state actually changes.
  // The win is for intermediate parents that pass `value` through React.memo'd
  // subtrees — they no longer re-render on unrelated parent state flips.
  /**
   * The user as consumers see it, with the staff flags resolved.
   *
   * Merged HERE rather than inside the user-doc listener because the two
   * documents arrive independently and either can update without the other;
   * folding staffRoles into `user` at the point of consumption keeps a single
   * answer without either listener having to know about the other.
   *
   * `staffRoles === null` means no staffRoles document, which is the
   * un-migrated case, so the legacy user-doc fields are consulted. Once
   * backfillPrivateSellerFields has run those fields are gone and both sides
   * read false. Cosmetic either way: assertDeveloper / assertAdmin re-check
   * server-side on every privileged callable, so a forged client value buys
   * nothing but a nav entry that leads to a permission-denied.
   */
  const userWithStaffRoles = useMemo(() => {
    if (!user) return user;
    return {
      ...user,
      // The owner-only settings, merged the same way (src/lib/private-settings.ts).
      settings: mergePrivateSettings(user.settings, privateLocale),
      mutedUids,
      isDeveloper: staffRoles ? staffRoles.isDeveloper : user.isDeveloper === true,
      isAdmin: staffRoles ? staffRoles.isAdmin : user.isAdmin === true,
    };
  }, [user, staffRoles, privateLocale, mutedUids]);

  const value: AuthContextType = useMemo(() => ({
    isAuthenticated: user !== null && user !== undefined,
    user: userWithStaffRoles,
    authUser: authUserState,
    authError,
    clearAuthError,
    isUserLoading,
    updateUser,
    signup, login, loginWithGoogle,
    logout,
    sellerTier,
    isDiamond,
    isFoundingSeller,
    staffRolesLoaded: !!authUserState && staffRolesFor === authUserState.uid,
    linkProvider, unlinkProvider, addPassword,
    muteUser, unmuteUser,
    mfaResolver, setMfaResolver,
    mfaLoginEmail, setMfaLoginEmail,
    start2faEnrollment,
    generateAndStoreRecoveryCodes,
    complete2faEnrollment,
    disable2fa,
    submitRecoveryCode,
    completeMfa,
    reauthenticateWithPassword,
    reauthenticateWithProvider,
    sendVerificationBeforeEmailUpdate,
    sendCurrentUserEmailVerification,
    refreshAuthUser,
    triggerProfileCreation,
    syncUserFromFirestoreNow,
  }), [
    user, userWithStaffRoles, authUserState, authError, clearAuthError, isUserLoading, updateUser,
    signup, login, loginWithGoogle, logout,
    sellerTier, isDiamond, isFoundingSeller, staffRolesFor,
    linkProvider, unlinkProvider, addPassword,
    muteUser, unmuteUser,
    mfaResolver, mfaLoginEmail,
    start2faEnrollment, generateAndStoreRecoveryCodes, complete2faEnrollment,
    disable2fa, submitRecoveryCode, completeMfa,
    reauthenticateWithPassword, reauthenticateWithProvider,
    sendVerificationBeforeEmailUpdate, sendCurrentUserEmailVerification,
    refreshAuthUser, triggerProfileCreation, syncUserFromFirestoreNow,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within a AuthProvider');
  }
  return context;
}
